const POST_LOGIN_REDIRECT_KEY = 'redirect_after_login'

/**
 * Accept only same-origin application paths. Browser auth recovery stores this
 * value outside React Router state because a hard redirect is required after a
 * failed token refresh.
 */
export const safePostLoginRedirect = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string' || !candidate.startsWith('/') || candidate.startsWith('//')) {
    return null
  }

  try {
    const parsed = new URL(candidate, window.location.origin)
    if (parsed.origin !== window.location.origin) return null

    const decodedPathname = decodeURIComponent(parsed.pathname)
    const normalizedPathname = decodedPathname.replace(/\/+$/, '') || '/'
    const authPathname = normalizedPathname.toLowerCase()
    if (authPathname === '/login' || authPathname === '/signup') return null

    const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`
    return normalized
  } catch {
    return null
  }
}

export const storePostLoginRedirect = (candidate: unknown): void => {
  const redirect = safePostLoginRedirect(candidate)
  try {
    if (redirect) {
      window.localStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirect)
    } else {
      window.localStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
    }
  } catch {
    // Storage is continuity state, not authentication authority. Login remains
    // available when a browser blocks or exhausts local storage.
  }
}

export const readPostLoginRedirect = (): string | null => {
  try {
    const stored = window.localStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    const redirect = safePostLoginRedirect(stored)
    if (!redirect && stored !== null) {
      window.localStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
    }
    return redirect
  } catch {
    return null
  }
}

export const clearPostLoginRedirect = (): void => {
  try {
    window.localStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  } catch {
    // A successful login must not fail because continuity storage is blocked.
  }
}
